import { useEffect, useState } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import axios from 'axios';

import Button from '../../components/Button';
import styles from '../../css/admin/Login.module.css';
import { useLanguage } from '../../hooks/LanguageContext';
import useAxiosAdminFetch from '../../hooks/useAxiosAdminFetch';
import Swal from 'sweetalert2';
import translations from './translations';
import { isApp } from '../../utils/isApp';

export default function UserLogin() {
  const { language } = useLanguage();
  const t = translations[language];
  // 아이디 저장 옵션이 켜져 있으면 저장된 아이디 불러오기
  const [saveId, setSaveId] = useState(
    () => localStorage.getItem('saveId') === 'true'
  );
  const [userId, setUserId] = useState(() => {
    if (localStorage.getItem('saveId') === 'true') {
      return (localStorage.getItem('savedUserId') || '').trim();
    }
    return '';
  });
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showWarning, setShowWarning] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { storeNo } = useParams();

  // 쿼리스트링에서 redirect 파라미터 가져오기
  const searchParams = new URLSearchParams(location.search);
  const redirectTo = searchParams.get('redirect');

  const [autoLogin, setAutoLogin] = useState(
    localStorage.getItem('autologin') === 'true'
  );
  const [storeInfo, setStoreInfo] = useState({});
  const { fetchData } = useAxiosAdminFetch();

  const shopAdminBase = (process.env.REACT_APP_SHOPADMIN_SERVER || '')
    .trim()
    .replace(/\/+$/, '');

  const buildShopAdminMobileLoginUrl = targetStoreNo => {
    if (!targetStoreNo) return '';
    const base = shopAdminBase || window.location.origin;
    return `${base}/mobile/login/${targetStoreNo}/normal`;
  };

  const escapeHtml = value =>
    String(value ?? '').replace(/[&<>"']/g, ch => {
      switch (ch) {
        case '&':
          return '&amp;';
        case '<':
          return '&lt;';
        case '>':
          return '&gt;';
        case '"':
          return '&quot;';
        case "'":
          return '&#39;';
        default:
          return ch;
      }
    });

  const handleAutoLoginToggle = () => {
    const newAutoLoginStatus = !autoLogin;
    setAutoLogin(newAutoLoginStatus);
    localStorage.setItem('autologin', newAutoLoginStatus);
  };

  // 중복 선언 제거, 아래에서 한 번만 선언

  // (중복 handleSaveIdToggle 제거)

  // 아이디 저장 체크박스 토글 함수
  const handleSaveIdToggle = () => {
    const newSaveIdStatus = !saveId;
    setSaveId(newSaveIdStatus);
    localStorage.setItem('saveId', newSaveIdStatus);
    if (!newSaveIdStatus) {
      localStorage.removeItem('savedUserId');
    } else {
      localStorage.setItem('savedUserId', String(userId || '').trim());
    }
  };

  /**
   * 로그인 후 리다이렉트 처리
   * - 쿼리스트링에 redirect 파라미터가 있으면 해당 경로로 이동
   * - 없으면 기본 페이지(giftstatus)로 이동
   *
   * 사용 예시:
   * - /login/1?redirect=/mobile/verifyconfirm/1/abc123/5
   * - /login/1?redirect=/mobile/sales/1
   * - /login/1 (redirect 없으면 기본 페이지로)
   */
  const routeAfterLogin = adminStoreNo => {
    // [APP-FORK] 앱에서만, 화면 이동 전에 FCM 토큰 등록을 띄운다.
    //   admin 은 로그인 후 navigate(SPA 내부 이동)라 문서가 다시 로드되지 않아
    //   appAuthReceiver 의 모듈 평가 시점 등록이 비로그인으로 걸러진 채 끝난다.
    //   ★await 하지 않는다 — 이동을 지연시키지 않고 실패는 무시한다.
    //   ★동적 import — isApp.js:22-23 과 같은 방식이라 웹 번들에 포함되지 않는다.
    if (isApp()) {
      import('../../utils/appAuthReceiver')
        .then(m => m.registerFcmIfNeeded())
        .catch(() => {});
    }
    if (redirectTo) {
      // 쿼리스트링에 redirect가 있으면 해당 경로로 이동
      navigate(decodeURIComponent(redirectTo));
    } else {
      // 기본 페이지로 이동
      navigate(`/mobile/giftstatus/${adminStoreNo}`);
    }
  };

  const handleLogin = async () => {
    const normalizedUserId = String(userId || '').trim();
    if (!normalizedUserId || !password) {
      setShowWarning(true);
      return;
    }

    try {
      const response = await fetchData({
        url: `${process.env.REACT_APP_API_SERVER}login`,
        method: 'post',
        data: {
          autoLogin: autoLogin,
          adminId: normalizedUserId,
          password: password,
        },
      });
      if (response?.data?.resultCode !== 200) {
        // useAxiosAdminFetch.fetchData는 실패해도 reject하지 않고 axios 에러의
        // response를 그대로 반환한다 — 실제 실패 사유는 response.status에 있다.
        const code = response?.status;
        const message =
          code === 403
            ? t.storeDeactivated
            : code === 401
              ? t.wrongPassword
              : code === 404
                ? t.userNotFound
                : t.loginFailedGeneric;
        Swal.fire({
          icon: 'error',
          title: t.loginFailed,
          text: message,
          confirmButtonText: t.confirm,
        });
        return;
      }
      // 로그인 성공 시 처리
      localStorage.setItem('admin_userid', normalizedUserId);
      // [2026-10-30 X-02] 비밀번호는 브라우저에 저장하지 않는다(읽는 곳 없음). 로그인 판정은 admin_userid + 서버 쿠키

      // 아이디 저장 옵션이 켜져 있으면 아이디 저장, 아니면 삭제
      if (saveId) {
        localStorage.setItem('savedUserId', normalizedUserId);
      } else {
        localStorage.removeItem('savedUserId');
      }
      // 로그인 직후 상세 정보로 상점 번호 검증
      const detail = await fetchData({
        url: `${process.env.REACT_APP_API_SERVER}detail/${encodeURIComponent(normalizedUserId)}`,
        method: 'get',
      });
      const detailStoreNo = detail?.data?.data?.storeNo;
      if (
        detailStoreNo &&
        storeNo &&
        String(detailStoreNo) === String(storeNo)
      ) {
        routeAfterLogin(String(detailStoreNo));
      } else {
        const targetUrl = buildShopAdminMobileLoginUrl(detailStoreNo);
        const safeUserId = escapeHtml(normalizedUserId);
        Swal.fire({
          icon: 'warning',
          title: t.storeMismatch,
          html: t.storeMismatchMessage.replace('{id}', safeUserId),
          showCancelButton: true,
          confirmButtonText: t.goToStore,
          cancelButtonText: t.confirm,
          reverseButtons: true,
        }).then(result => {
          if (result.isConfirmed && targetUrl) {
            window.location.href = targetUrl;
          }
        });
      }
    } catch (error) {
      const code = error?.response?.data?.resultCode || error?.response?.status;
      const message =
        code === 403
          ? t.storeDeactivated
          : code === 401
            ? t.wrongPassword
            : code === 404
              ? t.userNotFound
              : t.loginFailedGeneric;
      Swal.fire({
        icon: 'error',
        title: t.loginFailed,
        text: message,
        confirmButtonText: t.confirm,
      });
    }
  };

  useEffect(() => {
    const fetchStoreInfo = async () => {
      if (!storeNo) return;
      try {
        const response = await axios.get(
          `${process.env.REACT_APP_API_SERVER_PUBLIC}store/character/${storeNo}`
        );
        if (response?.data?.data) {
          setStoreInfo(response.data.data);
        }
      } catch (error) {
        console.error('Error fetching store information:', error);
      }
    };

    fetchStoreInfo();
  }, [storeNo]);

  useEffect(() => {
    const verifyExistingSession = async () => {
      const adminId = String(localStorage.getItem('admin_userid') || '').trim();
      if (!adminId) return;
      localStorage.setItem('admin_userid', adminId);
      try {
        const detail = await fetchData({
          url: `${process.env.REACT_APP_API_SERVER}detail/${encodeURIComponent(adminId)}`,
          method: 'get',
        });
        const detailStoreNo = detail?.data?.data?.storeNo;
        if (
          detailStoreNo &&
          storeNo &&
          String(detailStoreNo) === String(storeNo)
        ) {
          routeAfterLogin(String(detailStoreNo));
        } else if (detailStoreNo) {
          const targetUrl = buildShopAdminMobileLoginUrl(detailStoreNo);
          const safeAdminId = escapeHtml(adminId);
          Swal.fire({
            icon: 'warning',
            title: t.storeMismatch,
            html: t.storeMismatchMessage.replace('{id}', safeAdminId),
            showCancelButton: true,
            confirmButtonText: t.goToStore,
            cancelButtonText: t.confirm,
            reverseButtons: true,
          }).then(result => {
            if (result.isConfirmed && targetUrl) {
              window.location.href = targetUrl;
            }
          });
        }
      } catch (_) {}
    };

    verifyExistingSession();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className={styles.container}>
      <div className={styles.logoFrame}>
        <div className={styles.imageFrame}>
          <img
            className={styles.logo}
            src={storeInfo?.thumbnailFileUrl || '/images/default-logo.png'}
            alt="store logo"
          />
        </div>
        <div>
          <div className={styles.title}>{storeInfo?.storeName}</div>
        </div>
      </div>
      <div className={styles.formContainer}>
        <div className={styles.adminLogin}>{t.adminLogin}</div>
        <form
          className={styles.formFrame}
          onSubmit={e => {
            e.preventDefault();
            handleLogin();
          }}
        >
          <div className={styles.formFrame}>
            <input
              type="text"
              placeholder={t.loginUsername}
              value={userId}
              className={styles.input}
              onChange={e => setUserId(e.target.value)}
              autoComplete="username"
            />
            <div className={styles.passwordField}>
              <input
                type={showPassword ? 'text' : 'password'}
                placeholder={t.loginPassword}
                className={styles.input}
                value={password}
                onChange={e => setPassword(e.target.value)}
                autoComplete="current-password"
              />
              <button
                type="button"
                className={styles.passwordToggle}
                onClick={() => setShowPassword(v => !v)}
                aria-label={showPassword ? '비밀번호 숨기기' : '비밀번호 보기'}
              >
                {showPassword ? (
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24" /><line x1="1" y1="1" x2="23" y2="23" /></svg>
                ) : (
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8Z" /><circle cx="12" cy="12" r="3" /></svg>
                )}
              </button>
            </div>
            <button
              type="submit"
              style={{ display: 'none' }}
              aria-hidden="true"
            />
          </div>
          <div className={styles.optionContainer}>
            <div className={styles.optionFrame} onClick={handleSaveIdToggle}>
              <img
                className={styles.checked}
                src={saveId ? '/icons/checked.png' : '/icons/unchecked.png'}
                alt={saveId ? 'checked' : 'unchecked'}
              />
              <div className={styles.optionText}>{t.saveId}</div>
            </div>
            <div className={styles.optionFrame} onClick={handleAutoLoginToggle}>
              <img
                className={styles.checked}
                src={autoLogin ? '/icons/checked.png' : '/icons/unchecked.png'}
                alt={autoLogin ? 'checked' : 'unchecked'}
              />
              <div className={styles.optionText}>{t.autoLogin}</div>
            </div>
          </div>
          {showWarning && <div>{t.loginEnterBoth}</div>}
          <Button color="orange" onClick={handleLogin}>
            {t.login}
          </Button>
        </form>
      </div>
    </div>
  );
}
